import {
  Control,
  Controller,
  FieldErrors,
  UseFieldArrayRemove,
  UseFormSetValue,
  UseFormWatch
} from 'react-hook-form'

import { Icon, Textfield } from '@clientbase/clientbase-library'
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material'

import NumberFormatCustom from 'components/PorcentageInput/PorcentageInput'

import { currency } from 'utils'

import { theme } from 'styles/theme'

import { NewExpenseSchema } from '../../schema'
import CategoryAutocomplete from '../autocompletes/CategoryAutocomplete'
import CostCenterAutocomplete from '../autocompletes/CostCenterAutocomplete'

type AllocationRowProps = {
  index: number
  control: Control<NewExpenseSchema>
  watch: UseFormWatch<NewExpenseSchema>
  setValue: UseFormSetValue<NewExpenseSchema>
  errors: FieldErrors<NewExpenseSchema>
  totalValue: number
  enableRateio: boolean
  removeAllocation: UseFieldArrayRemove
  canRemove: boolean
}

const AllocationRow = ({
  index,
  control,
  setValue,
  errors,
  totalValue,
  enableRateio,
  removeAllocation,
  canRemove
}: AllocationRowProps) => {
  const isMobile = useMediaQuery('(max-width: 960px )')

  return (
    <Box
      sx={{
        display: 'grid',
        gap: '10px',
        gridTemplateColumns: isMobile
          ? '1fr'
          : enableRateio
          ? '1fr 1fr 0.8fr 0.7fr 40px'
          : '1fr 1fr 40px',
        alignItems: 'start'
      }}
    >
      <Controller
        control={control}
        name={`allocations.${index}.costCenter`}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          const errorTyped = error as any
          return (
            <CostCenterAutocomplete<NewExpenseSchema>
              key={value?.value || `cost-center-${index}`}
              value={value}
              onChange={(_: any, option: { label: string; value: string }) => {
                onChange(option ?? { value: '', label: '' })
              }}
              errorMessage={errorTyped?.label?.message}
              setValue={setValue}
            />
          )
        }}
      />

      <Controller
        control={control}
        name={`allocations.${index}.category`}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          const errorTyped = error as any
          return (
            <CategoryAutocomplete<NewExpenseSchema>
              key={value?.value || `category-${index}`}
              value={
                value as Omit<
                  { label: string; value: string; color: string },
                  'color'
                >
              }
              onChange={(_: any, option: { label: string; value: string }) => {
                onChange(option ?? { value: '', label: '' })
              }}
              errorMessage={errorTyped?.label?.message}
              setValue={setValue}
            />
          )
        }}
      />

      {enableRateio && (
        <>
          <Controller
            control={control}
            name={`allocations.${index}.amount`}
            render={({
              field: { onChange, value, ref },
              fieldState: { error }
            }) => (
              <Textfield
                customStartAdornment={
                  <Typography mr="6px" variant="smLight">
                    R$
                  </Typography>
                }
                inputRef={ref}
                helperText={error?.message}
                error={!!error?.message}
                label="Valor"
                mask="money"
                name="amount"
                placeholder="Digite o valor"
                startAdornment={true}
                onChange={(event) => {
                  // A máscara "money" trata os dígitos como centavos
                  // (ex.: "50" => R$ 0,50). Convertemos da mesma forma para
                  // manter o valor do form coerente com o que é exibido.
                  const digits = String(event.target.value).replace(/\D/g, '')
                  const amountNumber = digits ? Number(digits) / 100 : undefined
                  onChange(amountNumber)
                  setValue(
                    `allocations.${index}.percentage`,
                    totalValue > 0 && amountNumber != null
                      ? Number(((amountNumber / totalValue) * 100).toFixed(2))
                      : 0
                  )
                }}
                value={currency(value || '0')}
              />
            )}
          />

          <Controller
            control={control}
            name={`allocations.${index}.percentage`}
            render={({
              field: { onChange, value, ref },
              fieldState: { error }
            }) => (
              <Textfield
                inputRef={ref}
                helperText={error?.message}
                error={!!error?.message}
                label="%"
                name="percentage"
                placeholder="0"
                InputProps={{
                  inputComponent: NumberFormatCustom as any
                }}
                onChange={(event) => {
                  onChange(event)
                  // O NumberFormatCustom reemite o onChange (source 'prop')
                  // quando o `percentage` muda via setValue: edição do valor,
                  // mudança do total, "Distribuir igualmente". Esse eco só
                  // armazena — recalcular o `amount` a partir do percentual
                  // arredondado quantizaria o valor (bug de centavos quando o
                  // total é grande).
                  if ((event as { source?: string }).source === 'prop') return
                  const percentageNumber = Number(event.target.value) || 0
                  setValue(
                    `allocations.${index}.amount`,
                    Number(((totalValue * percentageNumber) / 100).toFixed(2))
                  )
                }}
                value={value != null ? String(value) : ''}
              />
            )}
          />
        </>
      )}

      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          pt: '19px'
        }}
      >
        <IconButton
          disabled={!canRemove}
          onClick={() => removeAllocation(index)}
        >
          <Icon
            icon="close"
            sx={{
              color: canRemove
                ? theme.palette.neutral[200]
                : theme.palette.neutral[600]
            }}
          />
        </IconButton>
      </Box>

      {!enableRateio && errors?.allocations?.[index]?.category && isMobile && (
        <Typography variant="xsBold" color="#990017" ml="5px">
          {(errors?.allocations?.[index]?.category as any)?.label?.message}
        </Typography>
      )}
    </Box>
  )
}

export default AllocationRow
